import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, MapPin, Star, Trash2 } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { errMsg } from "@/lib/api";

export const addressLine = (a) => [a.address, a.village, a.district && `Kec. ${a.district}`, a.city, a.province, a.postal_code].filter(Boolean).join(", ");

export function useSavedAddresses(enabled) {
  const call = useApi();
  const [list, setList] = useState([]);
  const load = useCallback(() => (enabled ? call("get", "/me/addresses").then(setList).catch(() => setList([])) : setList([])), [call, enabled]);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  return {
    list, load,
    save: (a) => call("post", "/me/addresses", a),
    setDefault: (id) => run(() => call("post", `/me/addresses/${id}/default`), "Alamat utama diperbarui"),
    remove: (id) => window.confirm("Hapus alamat ini?") && run(() => call("delete", `/me/addresses/${id}`), "Alamat dihapus"),
  };
}

export const SavedAddressPicker = ({ list, selectedId, onPick }) => (
  <div className="space-y-2 sm:col-span-2" data-testid="saved-address-picker">
    <p className="text-sm font-medium">Alamat tersimpan</p>
    <div className="grid gap-2 sm:grid-cols-2">
      {list.map((a) => (
        <button type="button" key={a.id} onClick={() => onPick(a)} data-testid={`saved-address-${a.id}`}
          className={`rounded-lg border p-3 text-left text-sm ${selectedId === a.id ? "border-amber-700 bg-amber-50" : "border-stone-200 hover:bg-stone-50"}`}>
          <p className="flex items-center gap-1.5 font-medium">{selectedId === a.id ? <Check className="h-4 w-4 text-amber-700" /> : <MapPin className="h-4 w-4 text-stone-400" />}
            {a.label || a.name}{a.is_default && <span className="rounded-full bg-amber-100 px-1.5 text-[10px] text-amber-900">Utama</span>}</p>
          <p className="mt-1 line-clamp-2 text-xs text-stone-500">{a.name} · {a.phone} — {addressLine(a)}</p>
        </button>
      ))}
    </div>
  </div>
);

export const SavedAddressManager = () => {
  const { list, setDefault, remove } = useSavedAddresses(true);
  return (
    <section className="mt-10" data-testid="saved-address-manager">
      <h2 className="mb-3 text-xl font-semibold">Alamat Tersimpan</h2>
      {list.length === 0 ? <p className="text-sm text-stone-500" data-testid="saved-address-empty">Belum ada alamat. Alamat akan tersimpan otomatis saat checkout.</p> : (
        <div className="divide-y divide-stone-100 rounded-lg border border-stone-200 bg-white">
          {list.map((a) => (
            <div key={a.id} className="flex items-start gap-3 p-4 text-sm" data-testid={`manage-address-${a.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium">{a.label || a.name} {a.is_default && <span className="ml-1 rounded-full bg-amber-100 px-2 text-xs text-amber-900">Utama</span>}</p>
                <p className="text-stone-500">{a.name} · {a.phone}</p>
                <p className="text-stone-500">{addressLine(a)}</p>
              </div>
              {!a.is_default && <button type="button" title="Jadikan utama" onClick={() => setDefault(a.id)} className="rounded p-2 text-stone-500 hover:bg-stone-100" data-testid={`address-default-${a.id}`}><Star className="h-4 w-4" /></button>}
              <button type="button" title="Hapus" onClick={() => remove(a.id)} className="rounded p-2 text-red-700 hover:bg-red-50" data-testid={`address-delete-${a.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
